import type { CustomListResponse, CustomModelSchema, CustomQuery, MetricData, PeriodSpec, SavedCustomMetric } from '@contracts';
import { resolvePeriod } from '@/lib/period';
import { authedFetch } from '@/lib/apiClient';

import { BRIDGE_URL as BRIDGE } from '@/lib/config';

/** Allowlist de modelos/campos para poblar el builder. */
export async function fetchCustomSchema(): Promise<CustomModelSchema[]> {
  const res = await authedFetch(`${BRIDGE}/metrics/custom/schema`);
  const body = await res.json();
  if (!body?.success) throw new Error(body?.message || 'No se pudo cargar el schema');
  return body.data as CustomModelSchema[];
}

/** Ejecuta una métrica personalizada; devuelve el shape del contrato (`{ data }`). */
export async function fetchCustomMetric(
  query: CustomQuery,
  period: PeriodSpec,
  companies: string[],
): Promise<{ data: MetricData }> {
  const { from, to } = resolvePeriod(period);
  const payload = {
    model: query.model,
    aggregation: query.aggregation,
    field: query.field ?? null,
    domain: query.domain,
    master_rules: query.masterRules ?? [],
    group_by: query.groupBy ?? null,
    group_by_interval: query.groupByInterval ?? null,
    group_limit: query.groupLimit ?? 20,
    visualization: query.visualization,
    date_field: query.dateField ?? null,
    companies: companies.join(','),
    periodFrom: from,
    periodTo: to,
  };
  const res = await authedFetch(`${BRIDGE}/metrics/custom`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await res.json();
  if (!body?.success) throw new Error(body?.message || 'Error en la métrica personalizada');
  return { data: body.data as MetricData };
}

/** Detalle (drill) de una métrica custom: las filas que componen su número, con sus
 *  mismos filtros/período/scope. El detalle hereda el dominio del cálculo; `detail`
 *  solo ajusta presentación (columnas/orden/límite) y filtros extra (avanzado). */
export async function fetchCustomList(
  query: CustomQuery,
  period: PeriodSpec,
  companies: string[],
): Promise<CustomListResponse> {
  const { from, to } = resolvePeriod(period);
  const d = query.detail;
  const payload = {
    model: query.model,
    aggregation: query.aggregation,
    field: query.field ?? null,
    domain: query.domain,
    master_rules: query.masterRules ?? [],
    group_by: null,
    date_field: query.dateField ?? null,
    detail: d
      ? {
          columns: d.columns ?? [],
          sort_by: d.sortBy ?? null,
          sort_dir: d.sortDir ?? 'desc',
          limit: d.limit ?? null,
          extra_domain: d.extraDomain ?? [],
        }
      : null,
    companies: companies.join(','),
    periodFrom: from,
    periodTo: to,
  };
  const res = await authedFetch(`${BRIDGE}/metrics/custom/list`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await res.json();
  if (!body?.success) throw new Error(body?.message || 'Error en el detalle de la métrica');
  return body.data as CustomListResponse;
}

/* ── Biblioteca de métricas guardadas (plantillas reutilizables) ─────────────── */

export async function fetchSavedMetrics(): Promise<SavedCustomMetric[]> {
  const res = await authedFetch(`${BRIDGE}/metrics/custom/definitions`);
  const body = await res.json();
  if (!body?.success) return [];
  return body.data as SavedCustomMetric[];
}

/** Inserta o reemplaza por id; devuelve la lista resultante. */
export async function saveMetricDef(def: SavedCustomMetric): Promise<SavedCustomMetric[]> {
  const res = await authedFetch(`${BRIDGE}/metrics/custom/definitions`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(def),
  });
  const body = await res.json();
  if (!body?.success) throw new Error(body?.message || 'No se pudo guardar la métrica');
  return body.data as SavedCustomMetric[];
}

export async function deleteMetricDef(id: string): Promise<SavedCustomMetric[]> {
  const res = await authedFetch(`${BRIDGE}/metrics/custom/definitions/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  const body = await res.json();
  if (!body?.success) throw new Error(body?.message || 'No se pudo eliminar la métrica');
  return body.data as SavedCustomMetric[];
}
